import { IComponentDefinition, IDisplayItem, IDisplayItemComponent } from "@kuika/kuika-cl-model";
import { KMainFunctions } from "../../../../shared/utility/main-functions";

/**
 * The structure tree's view model. One flat, already-ordered list of rows — the panel renders it
 * directly, so a render walks the subtree once instead of the three passes the sortable-tree
 * version needed (clone → definition rewrite → field decoration), and the live `IDisplayItem`s are
 * never mutated to carry presentation state.
 */
export interface IChildrenTreeNode {
  kuikaId: number;
  /** The display item this row stands for. Handed back to the panel's own move/delete/select calls. */
  di: IDisplayItem;
  /**
   * The row's label, and the parent segment of every add control's `data-testid`. Derived by
   * {@link deriveNodeTitle} — E2E scenarios type a value into the property grid and then address
   * the add button by the title that value produces, so the derivation is a contract.
   */
  title: string;
  /** The component definition's canonical name, or the stored one when no definition resolves. */
  componentName: string;
  iconUrl: string;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  /** Child component names this row may add right now, already filtered for target and duplicates. */
  accepts: string[];
  isSelected: boolean;
  isRoot: boolean;
  canDelete: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

/**
 * Everything the model needs from the screen-design context, narrowed to functions so the builder
 * stays testable without a provider. The panel supplies its own long-standing implementations —
 * accepted-children resolution and the move/delete rules are deliberately NOT reimplemented here.
 */
export interface IChildrenTreeDeps {
  getComponentDefinition: (item?: IDisplayItemComponent) => IComponentDefinition | undefined;
  /**
   * The panel's accepted-children resolver, already target- and duplicate-filtered. It is given the
   * row's canonical component AND the row's own display item, because a singleton child (a table's
   * header or row) is deduplicated against the children of the row being drawn.
   */
  getAccepts: (item: IDisplayItemComponent, di: IDisplayItem) => string[];
  isCollapsed: (kuikaId: number) => boolean;
  isSelected: (kuikaId: number) => boolean;
  canDelete: (di: IDisplayItem) => boolean;
  canMoveUp: (di: IDisplayItem) => boolean;
  canMoveDown: (di: IDisplayItem) => boolean;
}

/**
 * Columns hold their cell content without declaring it: their definitions carry no `childrenNames`,
 * so the generic descent rule would cut the subtree off at the column. They are named instead.
 */
const COLUMNS_WITHOUT_DECLARED_CHILDREN: string[] = ["TableTemplateColumn", "TableColumn", "StaticTableColumn"];

/**
 * The row label. The chain is load-bearing and intentionally asymmetric: a component with no
 * `titleAttributeName` yields `""`, not its component name — the add controls' `data-testid` keeps
 * that empty segment, and "fixing" it here would silently rename every testid in the E2E suite.
 */
export function deriveNodeTitle(di: IDisplayItem, definition: IComponentDefinition | undefined): string {
  const component: IDisplayItemComponent = di.item;
  if (!definition?.titleAttributeName) {
    return "";
  }
  const titleProperty = component.properties.find((p) => p.name === definition.titleAttributeName);
  const fixedValue = titleProperty?.symbol?.fixedValue;
  if (fixedValue) {
    return String(fixedValue).replace(/&nbsp;/g, " ");
  }
  if (di.friendlyName) {
    return di.friendlyName;
  }
  return component.name ?? "";
}

function canDescend(definition: IComponentDefinition | undefined, componentName: string): boolean {
  if (!definition) {
    return false;
  }
  const declaresChildren: boolean = !!definition.childrenNames && definition.childrenNames.length > 0;
  return declaresChildren || COLUMNS_WITHOUT_DECLARED_CHILDREN.includes(componentName);
}

function isRenderableChild(child: IDisplayItem, definition: IComponentDefinition | undefined): boolean {
  if (!child?.item?.name) {
    return false;
  }
  const declared: string[] | undefined = definition?.childrenNames;
  if (!declared) {
    return true;
  }
  return declared.indexOf(child.item.name) > -1;
}

/**
 * Flattens the subtree into rows, depth-first, skipping the children of a collapsed node entirely —
 * a closed 20-column table costs one row, not twenty hidden ones.
 */
export function buildChildrenTree(root: IDisplayItem | undefined, deps: IChildrenTreeDeps): IChildrenTreeNode[] {
  const rows: IChildrenTreeNode[] = [];
  if (!root?.item?.name) {
    return rows;
  }

  const visit = (di: IDisplayItem, depth: number, isRoot: boolean): void => {
    if (!di?.item?.name) {
      return;
    }
    const definition: IComponentDefinition | undefined = deps.getComponentDefinition(di.item);
    const componentName: string = definition?.name ?? di.item.name;
    // `getSelectedNodeStructure` branches on canonical component names ("Table", "ReportTable", …),
    // and a saved `item.name` may be stored in any casing — hand it the definition's spelling.
    const canonicalItem: IDisplayItemComponent = { ...di.item, name: componentName };
    const children: IDisplayItem[] = canDescend(definition, componentName)
      ? (di.children ?? []).filter((child) => isRenderableChild(child, definition))
      : [];
    const expanded: boolean = !deps.isCollapsed(di.id);

    rows.push({
      kuikaId: di.id,
      di,
      title: deriveNodeTitle(di, definition),
      componentName,
      iconUrl: KMainFunctions.getImageUrlByComponentDefinition(definition),
      depth,
      hasChildren: children.length > 0,
      expanded,
      accepts: deps.getAccepts(canonicalItem, di),
      isSelected: deps.isSelected(di.id),
      isRoot,
      canDelete: isRoot ? false : deps.canDelete(di),
      canMoveUp: isRoot ? false : deps.canMoveUp(di),
      canMoveDown: isRoot ? false : deps.canMoveDown(di)
    });

    if (!expanded) {
      return;
    }
    children.forEach((child) => visit(child, depth + 1, false));
  };

  visit(root, 0, true);
  return rows;
}
